import ImageLayer from 'ol/layer/Image';
import ImageArcGISRest from 'ol/source/ImageArcGISRest';
import { LayerManager } from 'models';

import { AbstractLayer, AbstractLayerProps, LayerProps } from './AbstractLayer';
import { MAP_PROJECTION } from 'config';

export interface Props extends AbstractLayerProps {
    config: LayerManager.LayerConfig;
    url?: string;
}

/**
 * The MapServerTileLayer is a rasterlayer that connects to an ArcGIS Server
 * instance, in particular a MapService.
 */
export class ImageArcGISRestLayer extends AbstractLayer<
    ImageLayer<ImageArcGISRest>
> {
    private date?: Date | null;

    private url: string | null;

    private source: ImageArcGISRest | null;

    constructor({ config, auth, opacity, visible, zIndex, url }: Props) {
        super({ config, opacity, visible, zIndex });
        this.auth = auth;
        this.url = url ?? null;
        this.source = this.buildSource();
    }

    initializeLayer(props: LayerProps) {
        return new ImageLayer({
            ...props,
            source: this.source || undefined,
        });
    }

    applyAuthentication() {
        if (this.auth && this.source) {
            this.source.setImageLoadFunction((image, src) => {
                const xhr = new XMLHttpRequest();
                xhr.responseType = 'blob';

                // Need it like this for the 'this' variable to work

                xhr.addEventListener('loadend', function () {
                    const data = this.response;
                    if (data !== undefined) {
                        try {
                            // @ts-ignore

                            image.getImage().src = URL.createObjectURL(data);
                        } catch (e) {
                            console.error(e);
                            image.getImage().dispatchEvent(new Event('error'));
                        }
                    } else {
                        image.getImage().dispatchEvent(new Event('error'));
                    }
                });
                xhr.addEventListener('error', () => {
                    image.getImage().dispatchEvent(new Event('error'));
                });
                xhr.open('GET', src);

                // Since these requests are not going
                // to our server, we don't use our token.
                if (this.auth && this.config.tokenRequired)
                    xhr.setRequestHeader(
                        'Authorization',
                        `${this.auth.tokenType} ${this.auth.accessToken}`,
                    );

                xhr.send();
            });
        }
    }

    private buildSource() {
        if (this.url == null || this.config?.mapserviceLayerId == null)
            return null;

        const SRS = MAP_PROJECTION.getCode();
        const source = new ImageArcGISRest({
            crossOrigin: 'anonymous',
            url: this.url,
            params: {
                LAYERS: `show:${this.config.mapserviceLayerId}`,
                LAYERDEFS: this.config?.layerDefs,
                TIME: this.date?.toISOString(),
                SRS,
            },
        });

        this.source?.dispose();
        this.source = source;
        if (this.layer) this.layer?.setSource(source);

        this.applyAuthentication();
        return source;
    }

    setFilter(filter: string) {
        this.filter = filter;
        // Add Layer Definition Filter
        if (this.source) {
            this.source.updateParams({ LAYERDEFS: this.filter });
        }
        this.source?.refresh();
    }

    refresh() {
        // Added timestamp parameter for force refreshing the layers instead of using cache
        this.source?.updateParams({ TIMESTAMP: Date.now() });
        this.source?.refresh();
    }
}

export default ImageArcGISRestLayer;
